import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ContextService } from '@app/core/services';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import { StepByStepWizardSteps } from '@shared/step-by-step-wizard';
import { AnyType, User, UserServiceContract } from 'lingo2-models';
import { first } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of } from 'rxjs';
import { mergeAll, take, takeUntil, tap } from 'rxjs/operators';
import { AbstractUserServiceCheckoutWizard } from '../models/abstract-user-service-checkout-wizard';

type ContextType = UserServiceContractCheckoutDialogComponent;

/** Мастер для оплаты ранее оформленного контракта на занятия */
@Component({
  selector: 'app-user-service-contract-checkout-dialog',
  templateUrl: './user-service-contract-checkout-dialog.component.html',
  styleUrls: ['./user-service-contract-checkout-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceContractCheckoutDialogComponent extends AbstractUserServiceCheckoutWizard {
  // DI
  protected readonly billingV2Service = inject(BillingV2Service);
  protected readonly deviceService = inject(DeviceDetectorService);
  protected readonly contextService = inject(ContextService);

  @Input()
  public set userServiceContractId(value: string) {
    this._userServiceContractId = value;
  }

  @Output()
  public closed = new EventEmitter<boolean>();

  @Output()
  public succeed = new EventEmitter<boolean>();

  public userServiceContract: UserServiceContract;
  public steps = new StepByStepWizardSteps<ContextType>([]);
  public userHavePaymentMethods = null;
  public payClicked = false;
  public openAddAccountModal = false;
  public addAccountProviderId: string;
  public afterAddAccountNeedUpdate: number;

  protected _me: User;
  protected _userServiceContractId: string;

  public get isOngoing(): boolean {
    return this.userServiceContract?.is?.ongoing;
  }

  public onClose() {
    this.closed.emit(true);
  }

  protected setupWizardSteps() {
    this.getUserPaymentMethods();
    this.loadUserServiceContract$(this._userServiceContractId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    /** @see onLoadUserServiceContractDone */
  }

  /** Донастройка мастера после загрузки контракта */
  protected onLoadUserServiceContractDone() {
    if (!this.userServiceContract) {
      return;
    }

    this._selectedTeacherId = first(this.userServiceContract.teacher_ids);
    this._schoolId = this.userServiceContract.school_id;

    if (this.isOwnService) {
      return this.activateView('own-service');
    }

    const init$: Array<Observable<AnyType>> = [];

    if (this.userServiceContract.visit_info?.unpaid_order) {
      // оплата ранее купленного или продление подвисшего
      this.userServiceContractOrder = this.userServiceContract.visit_info?.unpaid_order;
      this.setupPaymentWidget();
      this.activateView('payment');
    } else {
      if (!this.userServiceContract.can?.signup) {
        return this.activateView('cant-signup-contract');
      }

      // (авто)запись на курс
      init$.push(this.signupContract$(this.userServiceContract.id).pipe(take(1)));
      /** @see onSignupContractDone */
    }

    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => (this._me = me),
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    of(...[...init$, this.loadSchool$(this._schoolId), this.loadTeachers$([this._selectedTeacherId])])
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  /** Инициализация шагов мастера */
  protected initWizardSteps() {
    /* не требуется */
  }

  /** Выполнить обработку по активации шага мастера */
  protected onStepActivated(step: string) {
    /* не требуется */
  }

  /** Донастройка мастера после записи на услугу */
  protected onSignupContractDone() {
    if (this.userServiceContractOrder) {
      this.activateView('payment');
    }
  }

  public onPaymentConfigured(event) {
    super.onPaymentConfigured(event);
    this.detectChanges();
  }

  public onPaymentSucceed() {
    super.onPaymentSucceed();
    this.succeed.emit(true);
  }

  public getUserPaymentMethods() {
    this.billingV2Service
      .getAccounts()
      .pipe(tap(), takeUntil(this.destroyed$))
      .subscribe({
        next: (accounts) => {
          this.userHavePaymentMethods = accounts.length > 0;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.userHavePaymentMethods = false;
        },
      });
  }

  public onStartPayment() {
    this.payClicked = true;
    super.onStartPayment();
    this.detectChanges();
  }

  public onAddAccount(providerId: string) {
    this.openAddAccountModal = true;
    this.addAccountProviderId = providerId;
  }

  public onCloseAddAccount() {
    this.openAddAccountModal = false;
    this.afterAddAccountNeedUpdate = Date.now();
    this.detectChanges();
  }
}
